import { Component, OnInit, OnDestroy, Input, OnChanges } from '@angular/core';
import { DeviceService } from "../../device.service";
import { CommonService, TimeSource } from "src/app/shared/services/common.service";
import { TranslateService } from '@ngx-translate/core';
import { DatePipe } from '@angular/common';
import { UsersService } from 'src/app/users/users.service';
import * as lodash from 'lodash-es';
import * as moment from 'moment';
import { DeviceCommonService } from '../../device-common.service';
import { ColumnsConfig, TableConfig, TableData} from 'sct-custom-table/sct-table/projects/sct-table/src/lib/custom-table-interface';
import { DeviceFactory } from '../../device-factory/device-factory';
import { mainDeviceConfig } from '../../device-factory/device-config';
import { IotahDevice } from '../../device-factory/Iotah-device';
import { DeviceTypeService } from '../../device-type.service';
import { SitesService } from 'src/app/sites/sites.service';
import { SiteTemperatureUnit } from '../../../site-settings/site-utilization-settings/site-utilization-settings.interface';

type IMLiveRecordRow = { field: string, label: string, value: unknown };

@Component({
	selector: 'app-quick-view',
	templateUrl: './quick-view.component.html',
	styleUrls: ['./quick-view.component.css']
})
export class QuickViewComponent implements OnInit, OnChanges, OnDestroy {

	@Input() device: any= {};
	@Input() currentSite: any;
	@Input() enterprisePermissions: any = {};
	deviceConfig: mainDeviceConfig | null = (new IotahDevice).config;
	DeviceFactory = DeviceFactory;
	QV: any = {};
	QVNotCompressed: any = {};
	calcs: any;
	intervalID: any;
	warnings: any = [];

	currentGaugeNumbers: any = [];
	voltageGaugeNumbers: any = [];

	currentMinMax: any = {min: 0, max: 1000};
	voltageMinMax: any = {min: 0, max: 0};
	connectivityImage!: string;
	wifiImage!: string;
	oneDayDisconnect: number = 24 * 60 * 60;
	threeDaysDisconnect: number = 3 * (24 * 60 * 60);
	sevenDayDisconnect: number = 7 * (24 * 60 * 60);
	thirtyDayDisconnected: number = 30 * (24 * 60 * 60);
	sixMonths: number = this.oneDayDisconnect * 180;
	isAdminUser: Boolean;
	temperatureUnit: number = SiteTemperatureUnit.FAHRENHEIT;

	connectivityTooltipMessages = '';
	lastConnectTimeFields = [
		'metered_wifi_last_connect_time',
		'metered_cellular_last_connect_time',
		'non_metered_wifi_last_connect_time',
		'non_metered_cellular_last_connect_time',
	];

	tableData: TableData[] = [];
	tableConfig: TableConfig = {
		tableId: "quick_view_warning"
	}

	/** Which time zone each time column is rendered in. See ST-2326. */
	private readonly timeSourceByColumn: Record<string, TimeSource> = {
		// Angular DatePipe with no zone argument renders in the browser's zone.
		warning_raise_time: TimeSource.PcTime,
	};

	column: ColumnsConfig[] = [
		{key: 'warning_name', name : this.translate.instant('warnings.warning_name'), type: 'string'},
		{key: 'warning_raise_time', name: this.translate.instant('warnings.warning_raise_time'), type: 'date'}
	];

	gaugeData!: {
		currentGaugeValue: number,
		currentDegree: number,
		voltageDegree: number,
		currentData: { a: number, b: number, c: number },
		voltageData: { a: number, b: number, c: number }
	};

	has_phase_A: boolean = false;
	has_phase_B: boolean = false;
	has_phase_C: boolean = false;

	dataHaveDate = {
		qv: [
			'time'
		],
		live_rt: [
			'timestamp'
		],
		live_event: [
			'timestamp', 'end_timestamp'
		],
		glitches: [
			'timestamp_utc'
		]
	}

	dataToHide = {
			qv: [
				'wifi_ip',
				'eth_ip',
				'adc',
				'life_kwhr',
				'min_current',
			],
			live_rt: [
				'unused_1', 'isValidCRC16', 'crc16', 'unused_2', 'watt_hr_t_kwh', 'va_t_kva', 'var_t_kvar', 'active_power'
			],
			live_event: [
				'timestamp_utc', 'end_timestamp_utc', 'global_event_flags', 'event_flags', 'is_ok_record', 'isValidCRC16', 'version', 'calc_duration', 'study_id'
			],
			glitches: [
				'crc16', 'isValidCRC16'
			]
		};

	currentRange: { min: number, max: number } = { min: 0, max: 0 };
	voltageRange: { min: number, max: number } = { min: 0, max: 0 };
	imDeviceLiveEvent: any []= []
	imDeviceLiveRt: any []= []
	imDeviceInsight: [string, unknown][] = [];
	isIMDevice = false;

	// Kept off Insight: the live records have their own sections, load_control_status already shows
	// as Start/Stop and voltage/current as gauges on the meter panel above, top level
	// last_sim_connect_time is always 0 (the real value sits on dv), and the IoTEmeter portal lists
	// neither _duration nor Ethernet Status (the latter per ST-1008).
	imDevicesInsightExclude: string [] = [
		'eth_status',
		'_type',
		'_duration',
		'dv',
		'live_event',
		'live_rt',
		'glitches',
		'load_control_status',
		'last_sim_connect_time',
		'voltage',
		'current'
	];
	readonly fieldsForFloatNumbers = [
		'active_power_generated',
		'va_t_kva_generated',
		'var_t_kvar_generated',
		'watt_hr_t_kwh_generated',
		'active_power_consumed',
		'va_t_kva_consumed',
		'var_t_kvar_consumed',
		'watt_hr_t_kwh_consumed',
		'positive_peak_power',
		'negative_peak_power',
		'event_consumed_energy',
		'event_generated_energy',
		'power_event_value',
		'min_current',
		'max_current',
		'min_voltage',
		'max_voltage',
		'angle_va_vb',
		'angle_vb_vc',
		'angle_va_vc',
		'event_generated_kvarh',
		'event_consumed_kvarh',
		'min_voltage_ll',
		'max_voltage_ll'
	];
	onOffFields = [
		'remote_stop',
		'calibration_done',
		'light_sleep_mode'
	];

	// An IoTEmeter reports different hardware faults than a battery device, so the bits mean
	// something else here than they do in formatQv.
	readonly imHwStatusWarnings: Record<number, string> = {
		0x01: 'warnings.modbus',
		0x02: 'warnings.rtc',
		0x04: 'warnings.eth',
		0x08: 'warnings.flash_size',
		0x10: 'warnings.port_expander'
	};

	// IoTEmeter Live Event / Live RT display rules, mirrored from the IoTEmeter portal Quick View page.
	readonly imRtPhaseFields: { bit: number, fields: string[] }[] = [
		{ bit: 0x1, fields: ['current_a', 'voltage_a', 'voltage_a_ll', 'pf_a'] },
		{ bit: 0x2, fields: ['current_b', 'voltage_b', 'voltage_b_ll', 'pf_b'] },
		{ bit: 0x4, fields: ['current_c', 'voltage_c', 'voltage_c_ll', 'pf_c'] }
	];
	readonly imLineVoltageFields = ['voltage_a', 'voltage_b', 'voltage_c', 'min_voltage', 'max_voltage'];
	readonly imLiveRecordRawFields = ['sequence_id', 'version'];
	readonly imLiveRecordDurationFields = ['consumed_energy_duration', 'generated_energy_duration'];
	readonly imLiveRecordBooleanFields = [
		'ct_set',
		'first_time_configuration',
		'full_power_reset',
		'fw_update',
		'meter_changed',
		'midnight_split',
		'modbus_failed',
		'phase_a_ct_reversed',
		'phase_b_ct_reversed',
		'phase_c_ct_reversed',
		'set_rtc',
		'set_serial_number',
		'status_flag_safe_close',
		'system_start',
		'time_change_split',
		'update_config',
		'user_restart_request',
		'wiring_mode_set'
	];
	readonly imLiveRecordAbsFields = [
		'current_a',
		'current_b',
		'current_c',
		'max_current',
		'max_voltage',
		'max_voltage_ll',
		'min_current',
		'min_voltage',
		'min_voltage_ll',
		'modbus_cons_kwh',
		'modbus_gen_kwh',
		'voltage_a',
		'voltage_a_ll',
		'voltage_b',
		'voltage_b_ll',
		'voltage_c',
		'voltage_c_ll'
	];
	readonly imLiveRecordFloatFields = [
		'active_power_consumed',
		'active_power_generated',
		'event_consumed_energy',
		'event_consumed_kvarh',
		'event_generated_energy',
		'event_generated_kvarh',
		'max_current',
		'max_voltage',
		'max_voltage_ll',
		'min_current',
		'min_voltage',
		'min_voltage_ll',
		'negative_peak_power',
		'positive_peak_power',
		'va_t_kva_consumed',
		'va_t_kva_generated',
		'var_t_kvar_consumed',
		'var_t_kvar_generated',
		'watt_hr_t_kwh_consumed',
		'watt_hr_t_kwh_generated'
	];
	imDeviceMeterDV: any = {};

	constructor(
		public deviceService: DeviceService,
		public common: CommonService,
		private datePipe: DatePipe,
		public translate: TranslateService,
		public usersService: UsersService,
		public deviceCommonService: DeviceCommonService,
		public deviceTypeService: DeviceTypeService,
		private siteService: SitesService,
	) { }

	ngOnInit() {
		this.common.applyTimeSourceTooltips(this.column, this.timeSourceByColumn);

		let currentUser = this.usersService.getCurrentUser();
		this.isAdminUser = currentUser.is_admin;
		const deviceConfig = DeviceFactory.createDevice(this.device.config_info.sct_device_type);
		this.deviceConfig = deviceConfig.config;
		this.isIMDevice = DeviceFactory.isIMDeviceType(this.device.config_info.sct_device_type);

		this.intervalID = setInterval(() => {
			this.getQuickView();
		}, 3000); //every 3 seconds
		this.getQuickView();
		this.loadSiteTemperatureUnit();
		this.checkCurrecntLowRange();
		this.connectivityTooltipMessages = this.deviceService.getLastConnectionTimeMassage(this.device.config_info);
	}

	loadSiteTemperatureUnit() {
		if (!this.device.config_info?.site_id) return;

		this.siteService.getSiteUtilizationSettings(this.device.config_info.site_id).subscribe((res: any) => {
			const batteriesSettings = res?.[0]?.batteries_utilization_settings;
			if (!batteriesSettings)
				return;

			this.temperatureUnit = batteriesSettings.temperature_unit ?? SiteTemperatureUnit.FAHRENHEIT;
		});
	}

	checkCurrecntLowRange() {
			this.currentMinMax.max = this.device.config_info.has_current_low_range ? 750 : 1000;
	}

	getQuickView(noUiBlock=true) {
		if (!this.device.mac_address) return;
		this.deviceService.getQuickView(this.device.mac_address, noUiBlock).subscribe((data: any) => {
			this.QVNotCompressed = data.qv;
			this.QV = this.common.decompress(data.qv || {}, 'quick_view', this.device.config_info.sct_device_type);
			this.calcs = this.common.decompress(data.cv || {}, 'calcVars');
			this.imDeviceMeterDV = this.QV.dv;
			this.getQuickViewImages(this.QV.dv);

			if (this.showInsight()) {
				if (DeviceFactory.isIMDeviceType(this.device.config_info.sct_device_type)) {
					this.prepareIMDevicesQuickViewData(data);
					this.prepareWarnings(data);
				} else {
					this.prepareWarnings(data);
					this.prepareGauges();
					this.prepareData(data.nullifyCurrent);
				}
			}

			this.connectivityTooltipMessages = this.deviceService.getLastConnectionTimeMassage(this.device.config_info);
		});
	}

	getQuickViewImages(response) {
		this.wifiImage = '';
		this.connectivityImage = '';

		const nowTime = this.common.nowTime();
		const installatioDate = response.installation_date + this.oneDayDisconnect;

		// WIFI
		let wifiLastConnectTime = lodash.max([response.non_metered_wifi_last_connect_time, response.metered_wifi_last_connect_time]);
		let wifiDisconnectTime = nowTime - wifiLastConnectTime;
		let hasWifiImage = false;
		if (wifiLastConnectTime > installatioDate && wifiDisconnectTime < this.sixMonths) {
			hasWifiImage = true;
			this.showWifiImage(response);
		}

		// LTE
		const lteLastConnectTime = lodash.max([response.non_metered_cellular_last_connect_time, response.metered_cellular_last_connect_time]);
		const lteDisconnectTime = nowTime - lteLastConnectTime;
		let hasLteImage = false;
		if(lteLastConnectTime > installatioDate && lteDisconnectTime < this.sixMonths) {
			hasLteImage = true;
			this.showLteImage(nowTime, lteDisconnectTime, response)
		}

		if (!hasWifiImage && !hasLteImage) {
			if (lteLastConnectTime > wifiLastConnectTime) {
				this.showLteImage(nowTime, lteDisconnectTime, response)
			} else {
				this.showWifiImage(response);
			}
		}
	}

	showLteImage(nowTime, lastConnectTime, response) {
			if (response.enable_cellular === false)
				return;
			if (!response.cellular_iccid) {
				if (!this.usersService.userHasNOCAccess())
					return;

				// if the user has NOC permission and the device doesn’t have ICCID show the lte_bad
				this.connectivityImage = 'images/lte/lte_BAD.png';
				return;
			}
			if (lastConnectTime >= this.thirtyDayDisconnected) { // 30 days
				this.connectivityImage = 'images/lte/lte_disconnected_30d.png';
				return;
			}
			if (lastConnectTime >= this.sevenDayDisconnect) { // 7 days
				this.connectivityImage = 'images/lte/lte_disconnected_7d.png';
				return;
			}
			if (lastConnectTime >= this.threeDaysDisconnect) { // 3 days
				this.connectivityImage = 'images/lte/lte_disconnected_3d.png';
				return;
			}
			if (lastConnectTime >= this.oneDayDisconnect) { // 1 day

				const simCardTime = nowTime - response.last_sim_connect_time;
				// device connected for one day and less than 3 days we need to check SIM CARD DB
				if (simCardTime < this.oneDayDisconnect) {
					this.connectivityImage = 'images/lte/lte_connected.png';
					return;
				}
				this.connectivityImage = 'images/lte/lte_disconnected_3d.png';
				return;
			}
			this.connectivityImage = 'images/lte/lte_connected.png';
			return;
	}

	showWifiImage(response) {
		if (response._wifiConnected === true)
			this.wifiImage = '/images/wifi_connected.png';
		else
			this.wifiImage = '/images/wifi_not_connected_red.png';
	}

	showInsight() {

		let qvLength = Object.keys(this.QV).length;

		if(qvLength == 0)
			return false;

		if(qvLength == 1 && this.QV.hasOwnProperty('live_event'))
			return false;

		return true;
	}

	showBatteryNotConnected() {
		if(!DeviceFactory.isChargerDeviceType(this.device.config_info.sct_device_type))
			return false;

		if(this.QV._battery_connected === undefined)
			return false;

		const eventType = this.QV._type;
		const hasInvalidEventType = eventType !== undefined && eventType !== null && Number(eventType) === 0;

		return !this.QV._battery_connected || hasInvalidEventType;
	}

	showEvent() {
		if(!this.QV.live_event || Object.keys(this.QV.live_event).length == 0)
			return false;

		return true;
	}

	showCalcs() {
		if(!this.calcs || Object.keys(this.calcs).length == 0)
			return false;

		return true;
	}

	showRt() {
		if(!this.QV.live_rt || Object.keys(this.QV.live_rt).length == 0)
			return false;

		return true;
	}

	formatTime(val, type?, options:any={}) {
		let zoneId = this.QV.dv._zoneId;

		if(options.convertToTimestamp)
			val = moment(val).utc().unix();

		let time = this.common.getZoneTimestampFromUTC(zoneId, val);
		time = this.common.getDateFormattedFromUnixTimeStamp(time);

		if(options.timeOnly)
			return this.datePipe.transform(time, 'hh:mm:ss a');

		if(options.dateOnly){
			return this.datePipe.transform(time, 'MM/dd/yyyy');
		}
		return this.datePipe.transform(time, 'MM/dd/yyyy hh:mm:ss a');
	}

	formatNotes() {
		var flags = [
			'midnight_split',
			'time_change_split',
			'data_from_bv',
			'split_at_start',
			'split_at_end',
			'flag_finish',
			'flag_eq',
			'flag_finish_start',
			'flag_eq_start',
			'water_level_high',
			'temperature_exists'
		];

		var notes = [];

		flags.forEach((key) => {
			if(this.QV.live_event[key])
				notes.push(this.translate.instant('event.'+key));
		});

		return notes.join(', ');
	}

	getStaStatus() {

		let STA_STATUS_STARTED = 0,
		STA_STATUS_STOPPED = 1,
		STA_STATUS_CONNECTTING = 2,
		STA_STATUS_CONNECTED = 3,
		STA_STATUS_GOT_IP = 4,
		STA_STATUS_DISOCNNECTED = 5,
		STA_STATUS_AFTER_IP = 6,
		STA_STATUS_DISOCNNECTING = 7,
		STA_STATUS_DIS = 8,
		STA_STATUS_MAX = 9;

		if(this.QV.wifi_status == STA_STATUS_AFTER_IP && this.QV.wifi_ip != "0.0.0.0"){
			return this.translate.instant('qv.sta_connected')+' '+this.QV.wifi_ip;
		}

		return this.translate.instant('qv.sta_on');
	}

	formatQv(key, type="any", options={}) {
		let booleans = {
			'event': ['isValidCRC16'],
			'any': ['light_sleep_mode', 'low_range_is_inuse', 'battery_connected']
		};

		switch (type) {
			case "rt":
				switch(key) {
					case "timestamp":
						return this.formatTime(this.QV.live_rt[key]);
					break;
					case "rt_flag_current_error":
						return this.translate.instant(this.QV.live_rt[key] ? 'g.yes' : 'g.no');
					break;
					case "rt_el_level_flag":
						return this.translate.instant(this.QV.live_rt[key] ? 'g.high' : 'g.low');
					break;
				}
			break;
			case "event":
				switch(key) {
					case "event_type":
						if(this.deviceService.eventTypes[this.QV._type] !== undefined)
							return this.translate.instant('event.'+this.deviceService.eventTypes[this.QV._type]);
					break;
					case "finish_reason":
						if(this.deviceService.finishReasons[this.QV.live_event[key]] !== undefined)
							return this.translate.instant('event.'+this.deviceService.finishReasons[this.QV.live_event[key]]);
					break;
					case "eq_reason":
						if(this.deviceService.eqReasons[this.QV.live_event[key]] !== undefined)
							return this.translate.instant('event.'+this.deviceService.eqReasons[this.QV.live_event[key]]);
					break;
					case "my_zone_id":
						let zoneId = this.QV.live_event[key];
						return this.common.getZoneName(zoneId) + ' ['+zoneId+']';
					break;
					case "water_level_high":
						let waterLevel = this.QV.live_event[key] ? 'high' : 'low';
						return this.translate.instant('g.'+waterLevel);
					break;
					case "timestamp_utc":
					case "end_timestamp_utc":
						return this.formatTime(this.QV.live_event[key], 'event', options);
					break;
					case "zone_change":
						const zoneChange = this.QV.live_event[key] ? 'yes' : 'no';
						return this.translate.instant('g.'+zoneChange);
					break;
					case "notes":
						return this.formatNotes();
					break;
					case "hot_disconnection":
						const hotDisconnection = this.QV.live_event[key] ? 'yes' : 'no';
						return this.translate.instant('g.'+hotDisconnection);
					break;
					default:
						if(booleans['event'].includes(key))
							return !!this.QV.live_event[key];
					break;
				}
			break;

			default:
				switch(key) {
					case "time":
						return this.formatTime(this.QV[key]);
					break;
					case "hw_status":
						const statuses = {
							2: 'warnings.rtc',
							4: 'warnings.adc',
							8: 'warnings.flash_size',
							16: 'warnings.cellular_chip_hardware_failure',
							32: 'warnings.disabled_hall_effect'
						};

						const status = [];
						const hwValue = this.QV[key];

						for(const id in statuses) {
							if((hwValue & +id) != 0) {
								status.push(this.translate.instant(statuses[id]))
							}
						}

						return status.join(', ');
					break;
					case "readings":
						return this.formatReadings(key);
						break;
					case "readings":
						const readings = {
							"water_level": 'event.water_level',
							"water_flow": 'qv.water_flow',
							"voltage": 'rt.voltage',
							"current": 'rt.current',
							"temperature": 'rt.temperature',
							"current_low_range": "qv.current_low_range"
						};

						const reading = [];
						const value = this.QV[key];

						for (const item of value) {
							if (item == 'temperature' && !this.device.config_info.has_temperature_sensor)
								continue;

							if (item == 'water_level' && !this.device.config_info.has_water_level)
								continue;

							reading.push(this.translate.instant(readings[item]));
						}

						return reading.join(', ');
					break;

					case 'wifi_cellular_last_connect_time':
						return this.getLatestLastConnectTime();
					case 'el_level':
						const translateKey = this.QV[key] ? 'g.high' : 'g.low';
						return this.translate.instant(translateKey)
					default:
						if(booleans['any'].includes(key))
							return !!this.QV[key];
					break;
				}
			break;
		}
	}

	prepareData(nullifyCurrent){
		let ahrPercentage	= 0,
			kwhrPercentage	= 0,
			voltageDegree	= 0,
			currentDegree	= 0;

		let absCurrent = Math.abs(this.QV.current);
		let currentGaugeValue = (absCurrent <= 5 || nullifyCurrent) ? 0 : this.QV.current;
		if(absCurrent <= this.currentMinMax.min || currentGaugeValue == 0)
			currentDegree = 0;
		else if(absCurrent >= this.currentMinMax.max)
			currentDegree = -90;
		else {
			currentDegree = (absCurrent - this.currentMinMax.min) / (this.currentMinMax.max - this.currentMinMax.min);
			currentDegree = -1 * (currentDegree * 100 * (90/100));
		}

		if(this.QV.voltage <= this.voltageMinMax.min)
			voltageDegree = 0;
		else if(this.QV.voltage >= this.voltageMinMax.max)
			voltageDegree = -90;
		else {
			voltageDegree = (this.QV.voltage - this.voltageMinMax.min) / (this.voltageMinMax.max - this.voltageMinMax.min);
			voltageDegree = -1 * (voltageDegree * 100 * (90/100));
		}

		let eventTypeTxt= this.deviceService.eventTypes[this.QV._type];
		ahrPercentage	= ((eventTypeTxt == 'charge' ? this.QV._charge_as : this.QV._inuse_as) / 3600) / this.QV.dv._maxAHR;
		kwhrPercentage	= ((eventTypeTxt == 'charge' ? this.QV._charge_ws : this.QV._inuse_ws) / 3600000) / this.QV.dv._maxKWHR;

		ahrPercentage = (ahrPercentage > 1) ? 1 : ((ahrPercentage < 0) ? 0 : ahrPercentage);
		kwhrPercentage = (kwhrPercentage > 1) ? 1 : ((kwhrPercentage < 0) ? 0 : kwhrPercentage);

		const deviceConfig = DeviceFactory.createDevice(this.device.config_info.sct_device_type);
		const isStudyDevice = this.deviceTypeService.isStudyDevice(this.device.config_info);
		let eventImage = deviceConfig.getEventImage(eventTypeTxt, isStudyDevice);

		Object.assign(this.QV, {
			ahrPercentage: (1- ahrPercentage) * 100,
			kwhrPercentage: (1 - kwhrPercentage) * 100,
			currentGaugeValue,
			currentDegree,
			voltageDegree,
			eventImage,
			eventTypeTxt,
		});
	}

	prepareWarnings(data: any) {
		this.warnings = [];
		let warningList = [];

		// IM devices keep their warnings under their own keys mapper. The device type is passed only
		// for them because Iotah-device.ts declares a mapper override the backend iotah config does
		// not, so passing it for a truck device would read the wrong mapper.
		const isImDevice = DeviceFactory.isIMDeviceType(this.device.config_info.sct_device_type);
		const mapperOptions = isImDevice ? { deviceType: this.device.config_info.sct_device_type } : {};

		warningList = (data.warnings || []).map((item: any) => {
			item.warning = this.deviceService.getWarningsMabValues(item.warning_id, mapperOptions);
			return item;
		});

		const ignorePermissionList = this.deviceService.excludePermissionWarnings();

		for(const warningData of warningList) {
			// IM warning names have no entry in the permissions mapper, same as the site warnings page
			if (isImDevice || (ignorePermissionList.includes(warningData.warning)) || this.usersService.hasAccessPermission({ permission: this.enterprisePermissions.permission }, 'warnings.' + warningData.warning))
				this.warnings.push(warningData);
		}
		this.tableData = this.warnings.map((item: any) => {
			const warningValue = this.deviceService.getWarningsMabValues(item.warning_id, mapperOptions);
			return {
				row: { ...item, warning: warningValue },
				warning_name: { value: this.translate.instant(`warnings.${warningValue}`) },
				warning_raise_time: { value: this.datePipe.transform(item.trigger_time, 'long'), sortValue: item.trigger_time}
			};
		});
	}

	prepareGauges(){
		this.currentGaugeNumbers = [];
		this.voltageGaugeNumbers = [];
		let gaugeNumbers = [
			{top: '88px', left: '70px', val: 0},
			{top: '70px', left: '60px', val: 0},
			{top: '55px', left: '53px', val: 0},
			{top: '40px', left: '42px', val: 0},
			{top: '28px', left: '33px', val: 0},
			{top: '20px', left: '15px', val: 0},
			{top: '17px', left: '-4px', val: 0},
		];
		let currentSteps,
			voltageSteps;

		let deviceVPC = this.device.config_info.vpc;
		if(deviceVPC === undefined) {
			deviceVPC = (this.device.config_info.battery_type == this.deviceService.batteryTypesByName.lithuim_ion) ? 3 : 2;
		}

		this.voltageMinMax.min = +(1.7  * (deviceVPC / 2) * this.QV.dv._noc).toFixed(0);
		this.voltageMinMax.max = +(2.37 * (deviceVPC / 2) * this.QV.dv._noc).toFixed(0);

		currentSteps = (this.currentMinMax.max - this.currentMinMax.min) / 6;
		voltageSteps = (this.voltageMinMax.max - this.voltageMinMax.min) / 6;

		for (let index = 0; index < gaugeNumbers.length; index++) {
			let rowC = Object.assign({}, gaugeNumbers[index]);
			rowC.val = +((currentSteps*index) + this.currentMinMax.min).toFixed(0);
			this.currentGaugeNumbers.push(rowC);

			let rowV = Object.assign({}, gaugeNumbers[index]);
			rowV.val = +((voltageSteps*index) + this.voltageMinMax.min).toFixed(0);
			this.voltageGaugeNumbers.push(rowV);
		}
	}

	get waterLevelImage(): string {
		return this.QV.el_level ? '/sctportal/images/water_level_indicator/water_level_high.png' : '/sctportal/images/water_level_indicator/water_level_low.png';
	}
	
	getWaterLevelIndicator(): string {
		return this.QV.el_level ? this.translate.instant('g.high') : this.translate.instant('g.low');
	}

	showTemperatureSection(temperatureValue: number): boolean {
		return [
			this.DeviceFactory.types.iotah,
			this.DeviceFactory.types.battlink,
			this.DeviceFactory.types.battlinkII,
		].includes(this.device.config_info.sct_device_type) &&
			this.deviceConfig.fields.hasTemperatureSensor &&
			this.device.config_info?.has_temperature_sensor &&
			temperatureValue !== undefined &&
			!this.hasExternalTemperatureError();
	}

	hasExternalTemperatureError(): boolean {
		const deviceWarnings = this.device.config_info.device_warnings;
		return (deviceWarnings & 0x0040) != 0;
	}

	getLatestLastConnectTime() {
		let latestTime = '';
		let latestTimeField = '';

		for (const field of this.lastConnectTimeFields) {
			if (this.QV.dv[field] > latestTime) {
				latestTimeField = field;
				latestTime = this.QV.dv[field];
			}
		}
		return { label: latestTimeField, value: this.formatTime(latestTime) };
	}

	ngOnChanges() {
		if (this.device && this.currentSite)
			this.common.setPageTabTitle(`${this.currentSite.customer_name} - ${this.currentSite.name} - ${this.device.serial_number} - ${this.translate.instant('nav.quick_view')}`);

		if (this.device?.config_info) {
			const deviceConfig = DeviceFactory.createDevice(this.device.config_info.sct_device_type);
			this.deviceConfig = deviceConfig.config;
			this.isIMDevice = DeviceFactory.isIMDeviceType(this.device.config_info.sct_device_type);
		}

		return this.getQuickView(false);
	}

	prepareImDeviceData(nullifyCurrent: boolean) {
		let voltageDegree = 0,
			currentDegree = 0;

		let absCurrent = Math.abs(this.QV.current);
		let currentGaugeValue = (absCurrent <= 5 || nullifyCurrent) ? 0 : absCurrent;

		if (absCurrent <= this.currentRange.min || currentGaugeValue == 0)
			currentDegree = 0;
		else if (absCurrent >= this.currentRange.max)
			currentDegree = -90;
		else {
			currentDegree = (absCurrent - this.currentRange.min) / (this.currentRange.max - this.currentRange.min);
			currentDegree = -1 * (currentDegree * 100 * (90 / 100));
		}

		if (this.QV.voltage <= this.voltageRange.min)
			voltageDegree = 0;
		else if (this.QV.voltage >= this.voltageRange.max)
			voltageDegree = -90;
		else {
			voltageDegree = (this.QV.voltage - this.voltageRange.min) / (this.voltageRange.max - this.voltageRange.min);
			voltageDegree = -1 * (voltageDegree * 100 * (90 / 100));
		}

		this.gaugeData = {
			currentGaugeValue,
			currentDegree,
			voltageDegree,
			currentData: {
				a: this.QV.dv.current_a,
				b: this.QV.dv.current_b,
				c: this.QV.dv.current_c
			},
			voltageData: {
				a: this.QV.dv.voltage_a,
				b: this.QV.dv.voltage_b,
				c: this.QV.dv.voltage_c
			}
		};
		if (this.device.config_info.voltage_reference == this.deviceService.lineToLine) {
			this.gaugeData['voltageData'] = {
				a: +this.QV.dv.voltage_a.toFixed(2),
				b: +this.QV.dv.voltage_b.toFixed(2),
				c: +this.QV.dv.voltage_c.toFixed(2)
			};
		}
	}

	prepareIMDeviceGauges() {
		this.currentGaugeNumbers = [];
		this.voltageGaugeNumbers = [];
		let gaugeNumbers = [
			{ top: '88px', left: '70px', val: 0 },
			{ top: '70px', left: '60px', val: 0 },
			{ top: '55px', left: '53px', val: 0 },
			{ top: '40px', left: '42px', val: 0 },
			{ top: '28px', left: '33px', val: 0 },
			{ top: '20px', left: '15px', val: 0 },
			{ top: '17px', left: '-4px', val: 0 },
		];
		let currentSteps,
			voltageSteps;


		this.voltageRange.max = (this.device.config_info.voltage_reference === this.deviceService.lineToLine ? +this.calcVoltageReference(this.device.config_info.over_voltage_threshold, 'multi').toFixed(2) : this.device.config_info.over_voltage_threshold) * 1.2;
		this.currentRange.max = this.device.config_info.ct_ratio || 0;

		currentSteps = (this.currentRange.max - this.currentRange.min) / 6;
		voltageSteps = (this.voltageRange.max - this.voltageRange.min) / 6;

		for (let index = 0; index < gaugeNumbers.length; index++) {
			let rowC = Object.assign({}, gaugeNumbers[index]);
			rowC.val = +((currentSteps * index) + this.currentRange.min).toFixed(0);
			this.currentGaugeNumbers.push(rowC);

			let rowV = Object.assign({}, gaugeNumbers[index]);
			rowV.val = +((voltageSteps * index) + this.voltageRange.min).toFixed(0);
			this.voltageGaugeNumbers.push(rowV);
		}
	}

	prepareIMDevicesQuickViewData(data) {
		this.prepareIMDeviceLiveRecords();

		if (this.device.config_info.enabled_wires & 0x1)
			this.has_phase_A = true;

		if (this.device.config_info.enabled_wires & 0x2)
			this.has_phase_B = true;

		if (this.device.config_info.enabled_wires & 0x4) 
			this.has_phase_C = true;

		this.QV.client_socket_type = this.translate.instant('client_socket_type.' + this.QV.dv.client_socket_type);

		// The portal lists each connect time and the metered flag in Insight; ST keeps them on dv.
		for (const field of this.lastConnectTimeFields) {
			if (this.QV.dv[field])
				this.QV[field] = this.QV.dv[field];
		}

		if (this.QV.dv.is_metered_connection !== undefined)
			this.QV.is_metered_connection = this.QV.dv.is_metered_connection;

		for (const field in this.QV) {
			if (this.lastConnectTimeFields.includes(field)) {
				this.QV[field] = this.formatTime(this.QV[field]);
				continue;
			}

			if (field == 'wifi_status') {
				this.QV[field] = this.deviceCommonService.getApStatus(this.QV.wifi_status, this.QV.wifi_ip);
				continue;
			}

			if (field == 'setup_network_on') {
				this.QV[field] = !!this.QV[field];
				continue;
			}

			if (field == 'hw_status') {
				this.QV[field] = this.formatIMHwStatus(this.QV[field]);
				continue;
			}

			this.QV[field] = this.formatQv(field) || this.QV[field];

			if (this.fieldsForFloatNumbers.includes(field) && this.QV[field])
				this.QV[field] = (+this.QV[field]).toFixed(2);

			if (this.onOffFields.includes(field) && typeof this.QV[field] !== 'undefined')
				this.QV[field] = this.translate.instant(this.QV[field] ? 'g.on' : 'g.off');

			if (field == 'cellular_failure_type')
				this.QV[field] = this.translate.instant('failure_type.' + this.QV[field]);

			if (field == 'cellular_rssi')
				this.QV[field] = this.common.mapCellularRssi(Math.abs(this.QV[field]));
		}

		this.dataToHide.qv.forEach((item) => {
			delete this.QV[item]
		});

		this.imDeviceInsight = Object.entries(this.QV).filter(([key]) => !this.imDevicesInsightExclude.includes(key));

		this.prepareIMDeviceGauges();
		this.prepareImDeviceData(data.nullifyCurrent);
	}

	prepareIMDeviceLiveRecords() {
		this.imDeviceLiveEvent = this.buildIMLiveRecord(this.QV.live_event, 'live_event');
		this.imDeviceLiveRt = this.buildIMLiveRecord(this.QV.live_rt, 'live_rt');
	}

	/**
	 * Turns one IM live record into { field, label, value } rows for the Quick View template.
	 */
	buildIMLiveRecord(record: Record<string, unknown>, type: 'live_event' | 'live_rt'): IMLiveRecordRow[] {
		if (!record || typeof record != 'object')
			return [];

		const values = Object.assign({}, record);
		const isLineToLine = this.device.config_info.voltage_reference === this.deviceService.lineToLine;

		this.dataToHide[type].forEach((field) => delete values[field]);

		// Firmware padding, e.g. unused_2, unused_bit__3, unused_byte_1.
		for (const field in values) {
			if (/^unused/i.test(field))
				delete values[field];
		}

		if (type == 'live_rt') {
			delete values['version'];

			for (const phase of this.imRtPhaseFields) {
				if (!(this.device.config_info.enabled_wires & phase.bit))
					phase.fields.forEach((field) => delete values[field]);
			}
		}

		// Only one of the two readings is shown, and it carries its own label:
		// L-L meters keep the _ll field, L-N meters keep the plain one.
		for (const field of this.imLineVoltageFields)
			delete values[isLineToLine ? field : field + '_ll'];

		const rows: IMLiveRecordRow[] = [];
		for (const field in values) {
			rows.push({
				field,
				label: this.imLiveRecordLabelKey(field, type),
				value: this.formatIMLiveRecordValue(field, values[field], type)
			});
		}

		return rows;
	}

	/** Names each hardware fault an IoTEmeter reports, or says there is none. */
	formatIMHwStatus(value: number) {
		const faults = Object.keys(this.imHwStatusWarnings)
			.filter((bit) => (value & +bit) != 0)
			.map((bit) => this.translate.instant(this.imHwStatusWarnings[+bit]));

		return faults.length ? faults.join(', ') : this.translate.instant('warnings.no_error');
	}

	/** The portal times a live event in the zone the event recorded, not the device zone. */
	formatIMEventTime(value: number) {
		const zoneId = this.QV.live_event?.my_zone_id ?? this.QV.dv?._zoneId;

		let time = this.common.getZoneTimestampFromUTC(zoneId, value);
		time = this.common.getDateFormattedFromUnixTimeStamp(time);

		return this.datePipe.transform(time, 'MM/dd/yyyy hh:mm:ss a');
	}

	/** The portal shows energy durations to the second; common.timeFormat stops at minutes. */
	formatIMDuration(seconds: number) {
		const total = +seconds || 0;

		if (total >= 24 * 60 * 60)
			return '24:00:00';

		return moment('2015-01-01').startOf('day').seconds(total).format('HH:mm:ss');
	}

	/**
	 * The IM sections prefer an iotemeter.<section>.<field> label and fall back to the shared one,
	 * so their wording can match the IoTEmeter portal without touching labels other pages use.
	 */
	imLiveRecordLabelKey(field: string, type: 'live_event' | 'live_rt') {
		const section = type == 'live_event' ? 'events' : 'rt';
		const imKey = 'iotemeter.' + section + '.' + field;

		// instant() echoes the key back when nothing is translated for it.
		return this.translate.instant(imKey) === imKey ? section + '.' + field : imKey;
	}

	// value is any because it comes straight off the untyped QV payload and is handed to
	// helpers typed for numbers; narrowing it to unknown here would only add casts.
	formatIMLiveRecordValue(field: string, value: any, type: 'live_event' | 'live_rt') {
		if (field == 'runtime_warnings') {
			const warnings = this.deviceService.mapRunTimeWarning(value);
			return warnings.length ? warnings.map((warning) => this.translate.instant('warnings.' + warning)).join(', ') : '-';
		}

		if (field == 'rt_log_frequency')
			return this.deviceService.formatRtLogFrequencyField(this.device.config_info, value, true);

		if (this.dataHaveDate[type].includes(field))
			return type == 'live_event' ? this.formatIMEventTime(value) : this.formatTime(value);

		if (field == 'event_type')
			return this.translate.instant('events.im_event_type_' + (value || 0)); // default Daily

		if (value === null || value === undefined)
			return '-';

		if (field == 'my_zone_id')
			return this.common.getZoneName(value);

		if (this.imLiveRecordBooleanFields.includes(field))
			return this.translate.instant(value ? 'g.yes' : 'g.no');

		if (this.imLiveRecordDurationFields.includes(field))
			return this.formatIMDuration(value);

		if (typeof value != 'number')
			return value;

		let numericValue = value > this.common.FLOAT_VALUE_LIMIT ? 0 : value;

		if (this.imLiveRecordAbsFields.includes(field))
			numericValue = Math.abs(numericValue);

		if (this.imLiveRecordRawFields.includes(field))
			return numericValue;

		// Live RT shows every reading to 2 decimals; Live Event only its energy and power fields.
		if (type == 'live_rt' || this.imLiveRecordFloatFields.includes(field))
			return numericValue.toFixed(2);

		return numericValue;
	}

	calcVoltageReference = (value: number, type: 'divide' | 'multi') => {
		switch(type) {
			case 'divide':
				return value / Math.sqrt(3);
				break;
			case 'multi':
				return value * Math.sqrt(3);
				break;
			default:
				return value;
		}
	}

	private formatReadings(key: string): string {
  		const isIM = DeviceFactory.isIMDeviceType(this.device.config_info.sct_device_type);

		if (isIM) {
			const imDeviceReadings: Record<number, string> = {
				0x01: 'RS485',
				0x02: 'Button Pushed',
			};

			const readingValue = this.QV[key];
			const results: string[] = [];

			for (const code in imDeviceReadings) {
				if ((+code & readingValue) !== 0) {
					results.push(this.translate.instant(imDeviceReadings[code]));
				}
			}

			return results.length > 0 ? results.join(', ') : '-';
		} else {
			const readings: Record<string, string> = {
				water_level: 'event.water_level',
				water_flow: 'qv.water_flow',
				voltage: 'rt.voltage',
				current: 'rt.current',
				temperature: 'rt.temperature',
				current_low_range: 'qv.current_low_range',
			};

			const value: string[] = this.QV[key];
			const results: string[] = [];

			for (const item of value) {
				if (item === 'temperature' && !this.device.config_info.has_temperature_sensor) continue;
				if (item === 'water_level' && !this.device.config_info.has_water_level) continue;

				results.push(this.translate.instant(readings[item]));
			}

			return results.join(', ');
		}
	}

	ngOnDestroy() {
		clearInterval(this.intervalID);
	}
}
